/**
 * Unified authentication hook
 * Abstracts Auth0 vs API key authentication into a single interface
 */

import { useAuth0 } from '@auth0/auth0-react';
import { useAuthStore } from '../stores/authStore';
import { sessionStorage } from '../services/api/sessionStorage';
import { getAuthMode } from '../lib/env';

export interface UseAuth {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: {
    name?: string;
    email?: string;
    picture?: string;
  };
  loginWithRedirect?: () => void;
  logout: () => void;
  getToken: () => Promise<string | null>;
  error?: Error;
  authMode: 'AUTH0' | 'API_KEY';
}

/**
 * Unified auth hook - works with both Auth0 and API key authentication
 * Uses API_KEY mode if the env declares it (e.g. Docker without Auth0Provider)
 * OR if an API key credential is present in session (supports UI-driven switching)
 */
export function useAuth(): UseAuth {
  const authStore = useAuthStore();
  const auth0 = useAuth0(); // always called (hooks rules); only used in AUTH0 mode

  const session = sessionStorage.get();
  const hasApiKey = !!(session?.apiKey);

  if (getAuthMode() === 'API_KEY' || hasApiKey) {
    return {
      isAuthenticated: authStore.isAuthenticated,
      isLoading: authStore.loading,
      user: undefined,
      loginWithRedirect: undefined,
      logout: async () => {
        await authStore.logout();
      },
      getToken: async () => {
        const session = sessionStorage.get();
        return session?.apiKey || null;
      },
      error: authStore.error ? new Error(authStore.error) : undefined,
      authMode: 'API_KEY',
    };
  }

  // AUTH0 mode
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error,
  } = auth0;

  return {
    isAuthenticated,
    isLoading,
    user: user
      ? {
          name: user.name,
          email: user.email,
          picture: user.picture,
        }
      : undefined,
    loginWithRedirect,
    logout: () => {
      auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    },
    getToken: async () => {
      if (!isAuthenticated) {
        console.warn('[useAuth] Cannot get token: not authenticated');
        return null;
      }
      try {
        // getAccessTokenSilently returns the access token and transparently
        // refreshes it in the background (via refresh token) if it has expired.
        // Throws if the Auth0 session itself is fully expired, which falls into
        // the catch block → returns null → server returns 401 → handler fires.
        const accessToken = await getAccessTokenSilently();

        if (!accessToken) {
          console.error('[useAuth] Access token not returned by Auth0');
          return null;
        }

        return accessToken;
      } catch (error) {
        console.error('[useAuth] Failed to get Auth0 access token:', error);
        // If token retrieval fails, user might need to re-authenticate
        return null;
      }
    },
    error,
    authMode: 'AUTH0',
  };
}

/**
 * Token getter for use in HTTP client
 * This needs to be initialized from the app root after Auth0Provider is set up
 */
let tokenGetter: (() => Promise<string | null>) | null = null;

export function initializeTokenGetter(getter: () => Promise<string | null>): void {
  tokenGetter = getter;
}

export function getTokenGetter(): (() => Promise<string | null>) | null {
  return tokenGetter;
}
